import React, { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ChevronRight, ArrowRight, Sparkles, Sun, Moon } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import useCalendarStore from '../../store/useCalendarStore';

const Navbar = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isLoggedIn, setIsLoggedIn] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const darkMode = useCalendarStore(s => s.darkMode);
    const setDarkMode = useCalendarStore(s => s.setDarkMode);
    const isDarkPage = darkMode || location.pathname === '/' || location.pathname === '/portal';

    useEffect(() => {
        supabase.auth.getSession().then(({ data: { session } }) => {
            setIsLoggedIn(!!session);
        });

        const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
            setIsLoggedIn(!!session);
        });

        const handleScroll = () => {
            setScrolled(window.scrollY > 20);
        };

        window.addEventListener('scroll', handleScroll);
        return () => {
            subscription.unsubscribe();
            window.removeEventListener('scroll', handleScroll);
        };
    }, []);

    return (
        <nav className={`fixed top-0 left-0 right-0 z-[60] border-b transition-all duration-500 ${scrolled
            ? (isDarkPage
                ? 'border-white/10 bg-black/80 backdrop-blur-2xl py-4 shadow-[0_0_20px_rgba(124,93,255,0.15)]'
                : 'border-neutral-200 bg-white/80 backdrop-blur-2xl py-4 shadow-[0_0_20px_rgba(0,0,0,0.05)]')
            : 'border-transparent bg-transparent py-6'
            }`}>
            <div className="max-w-7xl mx-auto px-6 flex items-center justify-between">
                <div className="flex items-center gap-4 relative z-[60]">
                    <div className="relative group cursor-pointer flex items-center gap-5" onClick={() => navigate('/')}>
                        <div className="relative">
                            <div className="absolute -inset-2 bg-primary rounded-full blur opacity-20 group-hover:opacity-60 transition duration-500"></div>
                            <img src={isDarkPage ? "/logo.png" : "/logo_black.png"} alt="ScholarsAnchor" className="relative w-16 h-16 object-contain transition-all duration-500" />
                        </div>
                        <span className={`text-4xl font-black tracking-tighter font-display flex items-center gap-3 transition-colors duration-500 ${isDarkPage ? "text-white" : "text-neutral-900"
                            }`}>
                            ScholarsAnchor
                        </span>
                    </div>
                </div>

                <div className="hidden lg:flex items-center gap-10">
                    {[
                        { name: 'Capabilities', path: '/features' },
                        { name: 'Documentation', path: '/docs' },
                        { name: 'Student AI', path: '/portal' },
                        { name: 'About', path: '/about' }
                    ].map((item) => (
                        <Link
                            key={item.name}
                            to={item.path}
                            className={`text-sm font-semibold transition-all duration-300 tracking-tight font-display ${location.pathname === item.path
                                    ? 'text-primary'
                                    : (isDarkPage ? 'text-neutral-400 hover:text-white' : 'text-neutral-500 hover:text-neutral-900')
                                }`}
                        >
                            {item.name}
                        </Link>
                    ))}
                </div>

                <div className="flex items-center gap-6">
                    {location.pathname !== '/' && location.pathname !== '/portal' && (
                        <button
                            onClick={() => setDarkMode(!darkMode)}
                            className={`p-2.5 rounded-xl border transition-all duration-300 active:scale-95 flex items-center justify-center ${darkMode
                                    ? 'border-white/10 bg-white/5 hover:bg-white/10 text-white shadow-[0_0_15px_rgba(255,255,255,0.05)]'
                                    : 'border-neutral-200 bg-neutral-50 hover:bg-neutral-100 text-neutral-800 shadow-[0_0_15px_rgba(0,0,0,0.02)]'
                                }`}
                            title={darkMode ? "Switch to Light Mode" : "Switch to Dark Mode"}
                        >
                            {darkMode ? <Sun className="w-4 h-4 text-amber-400" /> : <Moon className="w-4 h-4 text-indigo-600" />}
                        </button>
                    )}

                    <Link
                        to={isLoggedIn ? '/dashboard/calendar' : '/login'}
                        className={`hidden sm:flex items-center gap-2 text-sm font-semibold transition-colors font-display ${isDarkPage ? 'text-neutral-400 hover:text-white' : 'text-neutral-500 hover:text-neutral-900'
                            }`}
                    >
                        {isLoggedIn ? 'Dashboard' : 'Sign In'}
                    </Link>

                    <Link
                        to="/signup"
                        className={`hidden sm:flex relative group px-6 py-2.5 rounded-xl overflow-hidden transition-all duration-300 active:scale-95 ${isDarkPage
                                ? 'bg-white shadow-[0_0_20px_rgba(255,255,255,0.1)] hover:shadow-[0_0_30px_rgba(124,93,255,0.3)]'
                                : 'bg-neutral-900 text-white hover:bg-neutral-850 shadow-[0_0_20px_rgba(0,0,0,0.15)]'
                            }`}
                    >
                        <span className={`relative text-sm font-bold font-display ${isDarkPage ? 'text-black' : 'text-white'}`}>
                            Early Access
                        </span>
                    </Link>

                    <button
                        onClick={() => setIsMenuOpen(!isMenuOpen)}
                        className="flex lg:hidden flex-col gap-1.5 p-2 focus:outline-none group z-[60]"
                    >
                        <div className={`h-0.5 w-6 transition-all duration-300 ${isMenuOpen ? 'rotate-45 translate-y-2 ' + (isDarkPage ? 'bg-white' : 'bg-black') : (isDarkPage ? 'bg-neutral-400 group-hover:bg-white' : 'bg-neutral-500 group-hover:bg-neutral-900')}`}></div>
                        <div className={`h-0.5 w-6 transition-all duration-300 ${isMenuOpen ? 'opacity-0' : (isDarkPage ? 'bg-neutral-400 group-hover:bg-white' : 'bg-neutral-500 group-hover:bg-neutral-900')}`}></div>
                        <div className={`h-0.5 w-6 transition-all duration-300 ${isMenuOpen ? '-rotate-45 -translate-y-2 ' + (isDarkPage ? 'bg-white' : 'bg-black') : (isDarkPage ? 'bg-neutral-400 group-hover:bg-white' : 'bg-neutral-500 group-hover:bg-neutral-900')}`}></div>
                    </button>
                </div>
            </div>

            {/* Mobile Menu Overlay */}
            <motion.div
                initial={false}
                animate={isMenuOpen ? { opacity: 1, x: 0, pointerEvents: 'auto' } : { opacity: 0, x: '100%', pointerEvents: 'none' }}
                className={`fixed inset-0 w-full h-screen backdrop-blur-3xl md:hidden z-50 pt-32 px-8 border-l transition-colors duration-500 ${isDarkPage
                        ? 'bg-black/95 border-white/10 shadow-[-20px_0_40px_rgba(0,0,0,0.5)]'
                        : 'bg-white/95 border-neutral-200 shadow-[-20px_0_40px_rgba(0,0,0,0.05)]'
                    }`}
            >
                <div className="flex flex-col gap-8">
                    {[
                        { name: 'Capabilities', path: '/features' },
                        { name: 'About', path: '/about' },
                        { name: 'Documentation', path: '/docs' },
                        { name: 'Student AI', path: '/portal' }
                    ].map((item) => (
                        <Link
                            key={item.name}
                            to={item.path}
                            onClick={() => setIsMenuOpen(false)}
                            className={`text-4xl font-extrabold transition-colors tracking-tighter font-display ${location.pathname === item.path
                                    ? 'text-primary'
                                    : (isDarkPage ? 'text-neutral-500 hover:text-white' : 'text-neutral-500 hover:text-neutral-900')
                                }`}
                        >
                            {item.name}
                        </Link>
                    ))}

                    <div className={`h-px w-full my-4 ${isDarkPage ? 'bg-white/5' : 'bg-neutral-200'}`}></div>

                    <Link
                        to={isLoggedIn ? '/dashboard/calendar' : '/login'}
                        onClick={() => setIsMenuOpen(false)}
                        className={`flex items-center justify-between p-8 rounded-3xl font-extrabold text-2xl transition-all group ${isDarkPage
                                ? 'bg-white text-black hover:shadow-[0_0_30px_rgba(124,93,255,0.4)]'
                                : 'bg-neutral-900 text-white hover:bg-neutral-800 shadow-[0_0_20px_rgba(0,0,0,0.15)]'
                            }`}
                    >
                        {isLoggedIn ? 'Dashboard' : 'Sign In'}
                        <ArrowRight className="w-8 h-8 group-hover:translate-x-2 transition-transform" />
                    </Link>
                </div>
            </motion.div>
        </nav>
    );
};

export default Navbar;
